import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { usePrivacyMode } from "hooks/usePrivacyMode";

/**
 * Hourly rate value cell.
 *
 * Privacy Mode aware: when the device-local privacy mode is ON, the value
 * is blurred by default and sits inside a grey pill (background, radius,
 * padding). Hovering or focusing the cell reveals the value; when the
 * pointer leaves or focus is lost, the value blurs again.
 * When privacy mode is OFF, the value is fully visible with no blur and
 * no pill chrome — just the plain number.
 *
 * Props:
 *  - amount: numeric value; formatted as "$X.XX" when `value` is not given.
 *  - value:  optional pre-formatted display string (e.g. "$1,234.56").
 *  - variant: Typography variant (default "body2").
 */
const HourlyRateHoverCell = ({ amount, value, variant = "body2" }) => {
  const theme = useTheme();
  const { privacyMode } = usePrivacyMode();
  const [revealed, setRevealed] = useState(false);
  const safeAmount = Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
  const display = value !== undefined && value !== null ? value : safeAmount;

  // Privacy Mode ON: blurred by default; hover/focus reveals, blur returns
  // when the pointer leaves / focus is lost. OFF: always plain.
  const isBlurred = privacyMode && !revealed;

  return (
    <Box
      tabIndex={0}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={() => setRevealed(false)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        // Grey pill chrome only in Privacy Mode — off means plain number.
        ...(privacyMode
          ? {
              borderRadius: "8px",
              px: 0.75,
              py: 0.25,
              backgroundColor: theme.palette.action.hover,
              cursor: "default",
              outline: "none",
            }
          : {}),
        "& .hourly-rate-value": {
          filter: isBlurred ? "blur(6px)" : "none",
          userSelect: isBlurred ? "none" : "text",
          transition: "filter 0.2s ease",
        },
      }}
    >
      <Typography variant={variant} className="hourly-rate-value">
        {display}
      </Typography>
    </Box>
  );
};

export default HourlyRateHoverCell;
